/*
 * Keeping the timer honest when the page is not in front.
 *
 * Browsers throttle main-thread timers in background tabs (once a second at first, about once a
 * minute after five minutes hidden) and do not run requestAnimationFrame at all while a page is
 * hidden or covered. So:
 *  - the clock is driven by a Web Worker, whose timers are not throttled;
 *  - if the timer ends while the page is hidden, an audible alarm and a system notification fire
 *    right away, and the full animation waits until the person is back (it needs frames to draw).
 * A phone app that is minimized cannot run any script, so the native build schedules a local
 * notification for the end time instead (see nativeAlarm.ts).
 */

export interface Ticker {
    stop: () => void;
}

/**
 * Calls `onTick` about every `everyMs`, from a worker thread where possible so that background tabs
 * do not slow it down. Falls back to a plain interval where workers are unavailable.
 */
export const createTicker = (onTick: () => void, everyMs = 250): Ticker => {
    if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined') {
        try {
            const url = URL.createObjectURL(
                new Blob([`setInterval(() => postMessage(0), ${everyMs});`], {
                    type: 'text/javascript',
                })
            );
            const worker = new Worker(url);
            worker.onmessage = onTick;
            return {
                stop: () => {
                    worker.terminate();
                    URL.revokeObjectURL(url);
                },
            };
        } catch {
            // A strict CSP can forbid blob workers: use the interval below.
        }
    }
    const id = window.setInterval(onTick, everyMs);
    return { stop: () => window.clearInterval(id) };
};

export const isPageHidden = (): boolean =>
    typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** Resolves once the page is visible again (at once if it already is). */
export const whenVisible = (): Promise<void> =>
    new Promise((resolve) => {
        if (!isPageHidden()) return resolve();
        const onChange = () => {
            if (!isPageHidden()) {
                document.removeEventListener('visibilitychange', onChange);
                resolve();
            }
        };
        document.addEventListener('visibilitychange', onChange);
    });

const ASKED_KEY = 'tomato-pop:asked-notifications';

/** Asks once, from a tap on Start, so a finished timer can reach the person when the tab is hidden. */
export const requestNotificationPermission = async (): Promise<void> => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    try {
        if (localStorage.getItem(ASKED_KEY)) return;
        localStorage.setItem(ASKED_KEY, 'true');
    } catch {
        // No storage: ask anyway, once per page load.
    }
    try {
        await Notification.requestPermission();
    } catch {
        // Old browsers use a callback form; not worth supporting.
    }
};

export interface DoneNotice {
    title: string;
    body: string;
    /** Button labels. Only shown when the notification goes through a service worker. */
    actions?: { startNext: string; dismiss: string };
}

/** Action ids the service worker reports back (see service-worker.ts). */
export const NOTIFY_START_NEXT = 'start-next';
/** Message posted by the service worker to the page when "start next" was pressed. */
export const START_NEXT_MESSAGE = 'TOMATO_START_NEXT';

/**
 * A system notification that the timer ended. Goes through the service worker when there is one
 * (the only form an installed Android PWA allows), otherwise a plain Notification. Never throws.
 */
export const notifyDone = async ({
    title,
    body,
    actions,
}: DoneNotice): Promise<void> => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    const options: NotificationOptions & {
        vibrate?: number[];
        renotify?: boolean;
        actions?: { action: string; title: string }[];
    } = {
        body,
        icon: '/logo192.png',
        badge: '/favicon.png',
        tag: 'tomato-pop-done',
        renotify: true,
        vibrate: [200, 100, 200, 100, 400],
    };
    try {
        const registration =
            'serviceWorker' in navigator
                ? await navigator.serviceWorker.getRegistration()
                : undefined;
        if (registration) {
            // Buttons exist only on notifications shown by a service worker.
            if (actions)
                options.actions = [
                    { action: NOTIFY_START_NEXT, title: actions.startNext },
                    { action: 'dismiss', title: actions.dismiss },
                ];
            await registration.showNotification(title, options);
        } else {
            new Notification(title, options);
        }
    } catch {
        // A notification that cannot show is not worth breaking the flow for.
    }
};

/** Keeps the screen on while the timer runs, so a phone does not suspend the page. Best effort. */
export interface WakeLockHandle {
    release: () => void;
}

export const keepScreenOn = (): WakeLockHandle => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wakeLock = (navigator as any).wakeLock;
    if (!wakeLock?.request) return { release: () => undefined };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let sentinel: any = null;
    let released = false;
    const acquire = async () => {
        if (released || isPageHidden()) return;
        try {
            sentinel = await wakeLock.request('screen');
        } catch {
            // Denied (battery saver, or no permission): the timer still works.
        }
    };
    // The browser drops the lock whenever the page is hidden, so take it again when it is back.
    const onVisible = () => {
        if (!isPageHidden() && (!sentinel || sentinel.released)) void acquire();
    };
    document.addEventListener('visibilitychange', onVisible);
    void acquire();

    return {
        release: () => {
            released = true;
            document.removeEventListener('visibilitychange', onVisible);
            void sentinel?.release?.().catch(() => undefined);
            sentinel = null;
        },
    };
};
